// kontaktStandardFilter.ts — Standardfilter der Kontaktliste (User 01.10.2026).
//
// „Sonstige Kontakte sollten zunächst ausgeblendet sein (Filter immer an:
//  Kunde und Interessent, auch mit Rolle). Wenn man sucht, werden trotzdem
//  alle durchsucht. Wenn man den Filter verändert, wird das für die Suche
//  danach berücksichtigt."
//
// Die Regel steht hier DOM-frei und geprüft (`kontaktStandardFilter.test.ts`),
// wie die Bereichs-Automatik in `kontaktsucheScope.ts` — in der 1.800-Zeilen-
// Komponente wäre sie verstreut über Anfangszustand, Filterobjekt,
// Arbeitsansicht und Zurücksetzen nicht mehr prüfbar.
//
// Kern ist die QUELLE des K-Status-Filters:
//   - "default": der Standard steht unangetastet drin. Er gilt fürs Blättern,
//     aber eine Suche ignoriert ihn und durchsucht alle Status.
//   - "user": der User hat den K-Status-Filter angefasst (angewendet ODER
//     geleert). Dann gilt, was er eingestellt hat — auch bei der Suche.
//     Ausnahme (User 02.10.2026): Steht genau Kunde + Interessent drin, ist das
//     immer der Standard und die Suche übergeht ihn (`standardAktiv`).

import type { TabellenVariante } from "./table/contactTableVarianten";
import {
  STANDARD_K_STATUS_WAHL,
  STANDARD_K_STATUS_WERT,
  gleicheFilterWahl,
  kStatusWahlAusFilterWert,
} from "./kontaktstatusWahl";

export type KStatusQuelle = "default" | "user";

export type FilterWerte = Record<string, string | boolean | undefined>;

/** Spalten-Id (= Filterschlüssel) des K-Status-Filters. */
export const K_STATUS_SPALTE = "Kontaktstatus";

/** Hat die Variante einen Standardfilter? Nur „Kontakte", nicht „CO intern". */
export const hatStandardFilter = (variante: TabellenVariante): boolean => variante === "kontakte";

/** Die Spaltenfilter, mit denen eine Variante frisch öffnet bzw. nach „Zurücksetzen" steht. */
export function standardFilterWerte(variante: TabellenVariante): FilterWerte {
  return hatStandardFilter(variante) ? { [K_STATUS_SPALTE]: STANDARD_K_STATUS_WERT } : {};
}

/**
 * Anfangszustand der Spaltenfilter.
 *
 * Mit Snapshot (Rücksprung aus der Akte) gilt der Snapshot — samt Quelle. Ein
 * Snapshot von VOR dem Standardfilter trägt keine Quelle: dann hat der User die
 * Filter so gesetzt, wie sie sind, also "user". Ohne Snapshot (frischer
 * Einstieg) der Standard der Variante.
 */
export function anfangsFilter(
  variante: TabellenVariante,
  snapshot: { filterValues?: FilterWerte; kStatusQuelle?: KStatusQuelle } | null | undefined,
): { filterValues: FilterWerte; kStatusQuelle: KStatusQuelle } {
  if (snapshot?.filterValues) {
    return { filterValues: snapshot.filterValues, kStatusQuelle: snapshot.kStatusQuelle ?? "user" };
  }
  return {
    filterValues: standardFilterWerte(variante),
    kStatusQuelle: hatStandardFilter(variante) ? "default" : "user",
  };
}

const istGesetzt = (v: string | boolean | undefined): boolean => v !== undefined && v !== "" && v !== false;

/** Ist der Wert genau der Standard (Kunde + Interessent, keine Rollen)? */
export function istStandardWert(v: string | boolean | undefined): boolean {
  return typeof v === "string" && v !== "" && gleicheFilterWahl(kStatusWahlAusFilterWert(v), STANDARD_K_STATUS_WAHL);
}

/**
 * Steht der K-Status-Filter als Standard drin?
 *
 * „Erstmal nur Kunden und Interessenten, damit es übersichtlicher ist — bei
 * der Suche werden alle Status angezeigt" (User 02.10.2026). Deshalb zählt der
 * Wert Kunde + Interessent IMMER als Standard, egal ob unangetastet, vom User
 * selbst so gewählt oder aus einem alten Tabellenstand ohne Quelle. Nur eine
 * andere Auswahl (z. B. nur Kunde, Sonstige, Rollen) gilt auch bei der Suche.
 */
export function standardAktiv(filterValues: FilterWerte, quelle: KStatusQuelle): boolean {
  const v = filterValues[K_STATUS_SPALTE];
  if (!istGesetzt(v)) return false;
  return quelle === "default" || istStandardWert(v);
}

/**
 * Die Spaltenfilter, die tatsächlich ans Backend gehen.
 *
 * Quelle "default" + nicht-leere Suche → der K-Status-Anteil fällt weg (die
 * Suche findet auch Sonstige); `standardFuerSucheAus` sagt der Oberfläche,
 * dass sie das sichtbar ansagen soll. Jeder andere Spaltenfilter gilt
 * unverändert.
 */
export function effektiveFilterWerte(args: {
  filterValues: FilterWerte;
  kStatusQuelle: KStatusQuelle;
  suchbegriff: string;
}): { werte: FilterWerte; standardFuerSucheAus: boolean } {
  const { filterValues, kStatusQuelle, suchbegriff } = args;
  if (standardAktiv(filterValues, kStatusQuelle) && suchbegriff.trim() !== "") {
    const { [K_STATUS_SPALTE]: _weg, ...rest } = filterValues;
    return { werte: rest, standardFuerSucheAus: true };
  }
  return { werte: filterValues, standardFuerSucheAus: false };
}

/**
 * Nur die Filter, die der User selbst gesetzt hat — für die Abschnitte der
 * Arbeitsansicht („Hat heute Geburtstag", „Neue Mail", „Verpasster Anruf"): Dort zählt jeder
 * Kontakt, auch ein sonstiger; der Standard blendet dort nichts aus.
 */
export function nurUserFilter(filterValues: FilterWerte, kStatusQuelle: KStatusQuelle): FilterWerte {
  if (!standardAktiv(filterValues, kStatusQuelle)) return filterValues;
  const { [K_STATUS_SPALTE]: _weg, ...rest } = filterValues;
  return rest;
}

/**
 * Wie viele Filter die Liste tatsächlich einschränken — Grundlage für den
 * Zähler am Filter-Knopf und für den Hinweis „ohne Filter N Ergebnisse".
 */
export function zaehlbareKontaktFilter(
  werte: FilterWerte,
  boFo: { boTeamIds: string[]; foUserIds: string[] },
): number {
  return (
    Object.values(werte).filter(istGesetzt).length +
    (boFo.boTeamIds.length + boFo.foUserIds.length > 0 ? 1 : 0)
  );
}

/**
 * Weicht der Zustand vom Standard der Variante ab? Dann bietet die Filterzeile
 * „Zurücksetzen" an. Der unangetastete Standard allein ist kein Grund dafür.
 */
export function weichtVomStandardAb(args: {
  variante: TabellenVariante;
  filterValues: FilterWerte;
  kStatusQuelle: KStatusQuelle;
  boFo: { boTeamIds: string[]; foUserIds: string[] };
}): boolean {
  const { variante, filterValues, kStatusQuelle, boFo } = args;
  if (boFo.boTeamIds.length + boFo.foUserIds.length > 0) return true;
  const standard = standardFilterWerte(variante);
  const gesetzt = Object.entries(filterValues).filter(([, v]) => istGesetzt(v));
  if (gesetzt.some(([k, v]) => standard[k] !== v)) return true;
  if (Object.keys(standard).some((k) => !istGesetzt(filterValues[k]))) return true;
  // Gleicher Wert wie der Standard: kein Grund für „Zurücksetzen", egal wer ihn gesetzt hat.
  void kStatusQuelle;
  return false;
}
